import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  Divider
} from '@mui/material';
import { Close, WhatsApp, Email, Send, ArrowBack, CheckCircle, Error as ErrorIcon, EmailOutlined, SendOutlined } from '@mui/icons-material';
import { downloadTicketPDF } from "../perfil/Compras/api";
import { config } from '../../services/config';

const TicketShareModal = ({ open, setShareModalOpen, onClose, ticketId, eventoNombre, ticketTipo, showLoader, ticketName }) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [emailSubject, setEmailSubject] = useState(`Te comparto un ticket para ${eventoNombre}`);
  const [emailBody, setEmailBody] = useState(`Hola,\n\nTe comparto un ticket para ${eventoNombre}.\n\nSaludos!`);
  const [shareOption, setShareOption] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const apiOrigin = config.URL_API;

  const handleOpen = () => setShareModalOpen(true);
  const handleClose = () => setShareModalOpen(false);

  const handleWhatsAppShare = async () => {
    try {
      showLoader(true);

      const response = await fetch(`${apiOrigin}/tickets/${ticketId}/share-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al generar enlace compartible');
      }

      const data = await response.json();
      const shareableLink = data.shareableLink;

      const shareMessage = `¡Hola! Te comparto el ticket para ${eventoNombre}.\n\nPuedes verlo y descargarlo desde este enlace: ${shareableLink}`;
      const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;

      window.open(whatsappUrl, '_blank');

      setTimeout(() => onClose(), 500);

    } catch (error) {
      console.error('Error al compartir por WhatsApp:', error);
      alert('Error al generar el enlace para compartir. Por favor intenta de nuevo.');
    } finally {
      showLoader(false);
    }
  };

  const handleEmailStep = () => {
    setShareOption('email');
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      setSendResult('error');
      return;
    }

    try {
      setSending(true);
      setSendResult(null);

      const pdfBlob = await downloadTicketPDF(ticketId);

      const fileName = `${eventoNombre.replace(/\s+/g, '_')}_${ticketTipo.replace(/\s+/g, '_')}_${ticketId}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('pdf', pdfFile);
      formData.append('recipient', emailAddress);
      formData.append('subject', emailSubject);
      formData.append('body', emailBody);
      formData.append('ticketId', ticketId);

      const response = await fetch(`${apiOrigin}/api/tickets/share-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al enviar el correo');
      }

      setSendResult('success');

      setTimeout(() => {
        onClose();
        setSendResult(null);
        setShareOption(null);
      }, 2000);

    } catch (error) {
      console.error('Error al enviar correo:', error);
      setSendResult('error');
    } finally {
      setSending(false);
    }
  };

  const handleBackToOptions = () => {
    setShareOption(null);
    setSendResult(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '10px'
          }
        }}
      >
        <DialogTitle sx={{}}>
          <Typography sx={{ fontSize: "18px" }} align="center" fontWeight={600}>
            Compartir Ticket
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              top: 8,
              /* right: 8 */
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "20px !important" }}>
          {!shareOption ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 4, textAlign: 'center' }}>
                Selecciona cómo quieres compartir tu ticket <br />{eventoNombre ? `para ${eventoNombre}` : ''}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: "center", alignItems: "center", gap: 3, mb: 4 }}>
                <Box
                  component="button"
                  onClick={handleWhatsAppShare}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'none', color: '#25D366' }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "center", width: 50, height: 50, mb: 1, borderRadius: '100%', backgroundColor: "#E6E6FF" }}>
                    <WhatsApp sx={{ fontSize: "28px" }} />
                  </Box>
                  <Typography variant="body2">WhatsApp</Typography>
                </Box>

                <Box
                  component="button"
                  onClick={handleEmailStep}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { textDecoration: 'none', color: '#E04D3B' }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "center", width: 50, height: 50, mb: 1, borderRadius: '100%', backgroundColor: "#E6E6FF" }}>
                    <EmailOutlined sx={{ fontSize: "28px" }} />
                  </Box>
                  <Typography variant="body2">Email</Typography>
                </Box>
              </Box>

              {/* <Box sx={{ mt: 3, backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
                  El PDF del ticket se descargará automáticamente para que lo tengas disponible.
                </Typography>
              </Box> */}
            </>
          ) : shareOption === 'email' && !sendResult ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  El sistema enviará un correo desde la cuenta oficial de PassGo con tu ticket adjunto.
                </Typography>

                <TextField
                  label="Correo del destinatario"
                  fullWidth
                  margin="normal"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  required
                  sx={{
                    fontFamily: "Bricolage Grotesque",
                    mb: 2,
                    '& .MuiInputBase-input-MuiOutlinedInput-input':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label.Mui-focused': {
                      color: '#5656FF',
                      fontFamily:"Bricolage Grotesque"
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#B2BAC2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#5656ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5656ff',
                      },
                    }
                  }}
                />

                <TextField
                  label="Asunto"
                  fullWidth
                  margin="normal"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  sx={{
                    fontFamily: "Bricolage Grotesque",
                    mb: 2,
                    '& .MuiInputBase-input-MuiOutlinedInput-input':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label.Mui-focused': {
                      color: '#5656FF',
                      fontFamily:"Bricolage Grotesque"
                    },
                    '& label':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#B2BAC2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#5656ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5656ff',
                      },
                    }
                  }}
                />

                <TextField
                  label="Mensaje"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  sx={{
                    fontFamily: "Bricolage Grotesque",
                    mb: 2,
                    '& .MuiInputBase-input-MuiOutlinedInput-input':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label.Mui-focused': {
                      color: '#5656FF',
                      fontFamily:"Bricolage Grotesque"
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#B2BAC2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#5656ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5656ff',
                      },
                    }
                  }}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: "10px" }}>
                <button
                  type="button"
                  onClick={handleBackToOptions}
                  style={{
                    border: "1px solid #5656FF",
                    color: "#5656FF",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                    width: "100%"
                  }}
                >
                  <ArrowBack /> Volver
                </button>

                <button
                  type="button"
                  disabled={sending || !emailAddress}
                  onClick={handleSendEmail}
                  style={{
                    backgroundColor: sending || !emailAddress ? "#CACACA" : "#5656ff",
                    color: "white",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    cursor: sending || !emailAddress ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                    width: "100%"
                  }}
                >
                  {sending ? (
                    <>
                      <CircularProgress size={16} sx={{ color: '#5656FF', marginRight: "4px" }} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <SendOutlined style={{ marginRight: "4px" }} />
                      Enviar correo
                    </>
                  )}
                </button>
              </Box>
            </>
          ) : shareOption === 'email' && sendResult && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              {sendResult === 'success' ? (
                <>
                  <CheckCircle sx={{ fontSize: 48, color: 'green', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>¡Correo enviado con éxito!</Typography>
                  <Typography variant="body2" color="textSecondary">
                    El ticket ha sido enviado a {emailAddress}
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorIcon sx={{ fontSize: 48, color: 'red', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>Error al enviar</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Por favor verifica la dirección de correo e inténtalo de nuevo.
                  </Typography>
                  <Button
                    color="primary"
                    onClick={handleBackToOptions}
                    sx={{ mt: 2 }}
                  >
                    Volver
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketShareModal;